import { reportAPI } from '@/api'
import { ApiResult } from '@modals/RequiredEmailConfirmation/components/RequiredEmailConfirmationModal/utils/api'
import { IIndividualStudentsItem } from '../../types'

const appendBaseFields = (data: FormData, item: IIndividualStudentsItem) => {
  data.append('student_id', String(item.student?.id))
  data.append('teacher_id', String(item.teacher?.id))
  data.append('course_id', String(item.course?.id))
}

export const handleApiResponse = <T>(res: any): ApiResult<T> => {
  const success =
    res?.data?.success === true ||
    res?.data?.status === 'success' ||
    res?.status === 200

  return {
    data: success ? res?.data?.data ?? true : null,
    success,
    error: res?.data?.error || res?.data?.message,
  }
}

export const handleApiError = (
  label: string,
  error: unknown,
): ApiResult<any> => {
  console.error(`Ошибка ${label}:`, error)

  return {
    data: null,
    success: false,
    error: error instanceof Error ? error.message : String(error),
  }
}

export const updateIndividualStudentLessonDuration = async (
  item: IIndividualStudentsItem,
  lessonDuration: number,
): Promise<ApiResult<boolean>> => {
  try {
    const data = new FormData()
    appendBaseFields(data, item)
    data.append('lesson_duration', String(lessonDuration))

    const res = await reportAPI.private.updateLessonDuration(data)

    return handleApiResponse<boolean>(res)
  } catch (error) {
    return handleApiError('обновления стандартной длительности', error)
  }
}

export const updateIndividualStudentSalary = async (
  item: IIndividualStudentsItem,
  salary: number,
  effectiveFrom?: string,
): Promise<ApiResult<boolean>> => {
  try {
    const data = new FormData()
    appendBaseFields(data, item)
    data.append('salary', String(salary))
    if (effectiveFrom) data.append('effective_from', effectiveFrom)

    const res = await reportAPI.private.updateSalary(data)

    return handleApiResponse<boolean>(res)
  } catch (error) {
    return handleApiError('обновления ЗП', error)
  }
}

export const updateIndividualStudentCostForStudent = async (
  item: IIndividualStudentsItem,
  costForStudent: number,
): Promise<ApiResult<boolean>> => {
  try {
    const data = new FormData()
    appendBaseFields(data, item)
    data.append('cost_for_student', String(costForStudent))

    const res = await reportAPI.private.updateCostForStudent(data)

    return handleApiResponse<boolean>(res)
  } catch (error) {
    return handleApiError('обновления стоимости для ученика', error)
  }
}

export const updateIndividualStudent = async ({
  item,
  teacherId,
  lessonDuration,
  salary,
  costForStudent,
  salaryEffectiveFrom,
}: {
  item: IIndividualStudentsItem
  teacherId: number
  lessonDuration: number
  salary: number
  costForStudent: number
  salaryEffectiveFrom?: string
}): Promise<ApiResult<boolean>> => {
  try {
    const data = new FormData()
    data.append('student_id', String(item.student?.id))
    data.append('course_id', String(item.course?.id))
    data.append('teacher_id', String(teacherId))
    data.append('lesson_duration', String(lessonDuration))
    data.append('salary', String(salary))
    data.append('cost_for_student', String(costForStudent))
    if (salaryEffectiveFrom) data.append('salary_effective_from', salaryEffectiveFrom)

    const res = await reportAPI.private.updateStudent(data)

    return handleApiResponse<boolean>(res)
  } catch (error) {
    return handleApiError('обновления данных индивидуального учащегося', error)
  }
}

export const getIndividualStudentsUnassignedCount = async (): Promise<
  ApiResult<number>
> => {
  try {
    const res = await reportAPI.private.studentsUnassignedCount()
    const count =
      res?.data?.data?.count ??
      res?.data?.count ??
      res?.data?.data ??
      res?.data ??
      0

    return {
      data: Number(count) || 0,
      success: true,
      error: undefined,
    }
  } catch (error) {
    return handleApiError('получения количества нераспределенных', error)
  }
}

export const updateIndividualStudentTeacher = async (
  item: IIndividualStudentsItem,
  teacherId: number,
): Promise<ApiResult<boolean>> => {
  try {
    const data = new FormData()
    data.append('student_id', String(item.student?.id))
    data.append('course_id', String(item.course?.id))
    data.append('teacher_id', String(teacherId))

    const res = await reportAPI.private.updateTeacher(data)

    return handleApiResponse<boolean>(res)
  } catch (error) {
    return handleApiError('назначения педагога', error)
  }
}
